import { FC, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  TextField,
} from '@mui/material'
import { useMotivosPerda } from './useMotivosPerda'
import { DadosPerda, ErroMotivoPerda } from '@/types/scoreComercial'

export type DestinoPerda = 'CANCELADA' | 'NAO_CONVERTIDA'

interface MotivoPerdaDialogProps {
  open: boolean
  empresa?: string
  /** Só muda o texto de apoio: cancelar (consultor/back-office) ou não converter (back-office). */
  destino?: DestinoPerda
  submitting?: boolean
  /**
   * Código de erro devolvido pelo backend na última tentativa — o diálogo aponta o campo
   * certo (`MOTIVO_PERDA_OBRIGATORIO` → motivo; `MOTIVO_PERDA_OBSERVACAO_OBRIGATORIA` →
   * observação). Quem chama limpa ao fechar/reabrir.
   */
  erro?: ErroMotivoPerda | null
  onClose: () => void
  /** Chamado só com motivo do catálogo escolhido (e observação, quando o motivo exige). */
  onConfirm: (dados: DadosPerda) => void
}

/** Mesmo limite do backend (`AtualizarStatusRequest.observacao @Size(max = 4000)`). */
export const OBSERVACAO_MAX = 4000

const TEXTO_DESTINO: Record<DestinoPerda, string> = {
  CANCELADA: 'será cancelada',
  NAO_CONVERTIDA: 'será marcada como não convertida',
}

/**
 * Mover um card para "Perdido" exige um motivo PADRONIZADO (decisão 8.6, PDF PIPE-04): o
 * select vem do catálogo `motivos_perda` (dado do ADMIN, nunca enum aqui) e a observação é
 * obrigatória quando o motivo pede (`exigeObservacao`, ex.: "Outro"). O backend valida de
 * novo — para consultor e back-office — e devolve o código do campo que faltou.
 */
export const MotivoPerdaDialog: FC<MotivoPerdaDialogProps> = ({
  open,
  empresa,
  destino = 'CANCELADA',
  submitting = false,
  erro = null,
  onClose,
  onConfirm,
}) => {
  const { motivos, carregando, erro: erroCatalogo, recarregar } = useMotivosPerda(open)
  const [codigo, setCodigo] = useState('')
  const [observacao, setObservacao] = useState('')

  // Cada abertura começa limpa — motivo de uma perda não serve para a próxima.
  useEffect(() => {
    if (open) {
      setCodigo('')
      setObservacao('')
    }
  }, [open])

  const motivo = useMemo(() => motivos.find((m) => m.codigo === codigo), [motivos, codigo])
  const exigeObservacao = !!motivo?.exigeObservacao
  const observacaoOk = !exigeObservacao || observacao.trim().length > 0
  const valido = !!motivo && observacaoOk

  const erroMotivo = erro === 'MOTIVO_PERDA_OBRIGATORIO'
  const erroObservacao = erro === 'MOTIVO_PERDA_OBSERVACAO_OBRIGATORIA'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Marcar como perdida</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {empresa ? `A reserva de ${empresa} ${TEXTO_DESTINO[destino]}.` : `A reserva ${TEXTO_DESTINO[destino]}.`}{' '}
          Escolha o motivo — ele fica registrado no histórico.
        </DialogContentText>

        {carregando && <LinearProgress aria-label="Carregando motivos" sx={{ mb: 2 }} />}

        {!carregando && erroCatalogo && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => void recarregar()}>
                Tentar novamente
              </Button>
            }
          >
            Não foi possível carregar os motivos de perda. {erroCatalogo}
          </Alert>
        )}

        {!carregando && !erroCatalogo && motivos.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Nenhum motivo de perda cadastrado — peça ao administrador para cadastrar o catálogo.
          </Alert>
        )}

        <TextField
          select
          SelectProps={{ native: true }}
          label="Motivo da perda"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          fullWidth
          required
          autoFocus
          disabled={carregando || motivos.length === 0}
          error={erroMotivo}
          helperText={erroMotivo ? 'Escolha um motivo do catálogo' : ' '}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 1.5 }}
        >
          <option value="">Selecione…</option>
          {motivos.map((m) => (
            <option key={m.codigo} value={m.codigo}>
              {m.rotulo}
            </option>
          ))}
        </TextField>

        <TextField
          label="Observação"
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          multiline
          rows={3}
          fullWidth
          required={exigeObservacao}
          // Limite do servidor aplicado aqui: sem isto o 4001º caractere viraria um
          // VALIDATION_ERROR genérico depois do clique, em vez de um contador antes dele.
          inputProps={{ maxLength: OBSERVACAO_MAX }}
          error={erroObservacao || (exigeObservacao && !observacaoOk && observacao.length > 0)}
          helperText={`${
            erroObservacao
              ? 'Este motivo exige uma observação'
              : exigeObservacao
                ? 'Obrigatória para este motivo'
                : 'Opcional'
          } · ${observacao.length}/${OBSERVACAO_MAX}`}
          placeholder="Ex.: cliente desistiu da operação, fechou com outro parceiro…"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          Voltar
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={!valido || submitting}
          onClick={() =>
            onConfirm({
              motivoPerdaCodigo: codigo,
              observacao: observacao.trim() || undefined,
            })
          }
        >
          {submitting ? 'Registrando…' : 'Marcar como perdida'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
